import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Instagram, Globe, ArrowLeft, Camera, User } from "lucide-react";
import api, { FILE_BASE } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PhotoCard from "../components/PhotoCard";
import PhotoLightbox from "../components/PhotoLightbox";
import BundleBanner from "../components/BundleBanner";
import Seo from "../components/Seo";
import NotFoundView from "../components/NotFoundView";

export default function PublicPhotographerProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api.get(`/public/photographers/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Fotografen hittades inte"));
  }, [slug]);

  if (err) return <NotFoundView message={err} />;

  if (!data) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-12 text-white/50">Laddar...</div>
        <Footer />
      </div>
    );
  }

  const { photographer: p, photos } = data;
  // If all visible photos share the same unit price, surface that to the bundle banner
  const profileUnitPrice = (() => {
    if (!photos.length) return null;
    const prices = new Set(photos.map((ph) => ph.price_sek));
    return prices.size === 1 ? photos[0].price_sek : null;
  })();

  return (
    <div className="App">
      <Seo
        title={`${p.name} — Enduro-fotograf · Enduroskola`}
        description={`${p.bio_short || `Enduro-fotograf på Enduroskola.`} Se ${photos.length} foton från ${p.name}, sök på startnummer och köp direkt.`}
        path={`/fotograf/${p.slug}`}
      />
      <Header />

      {/* Hero */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="w-32 h-32 sm:w-40 sm:h-40 bg-[#1a1a1a] overflow-hidden border border-white/10 flex items-center justify-center flex-shrink-0">
            {p.profile_image_url ? (
              <img src={`${FILE_BASE}${p.profile_image_url}`} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <User className="w-16 h-16 text-white/20" />
            )}
          </div>
          <div>
            <p className="label-overline mb-2">Fotograf · {p.photo_count} foto{p.photo_count === 1 ? "" : "n"}</p>
            <h1 className="font-display font-black uppercase text-white text-4xl sm:text-6xl leading-[0.95] mb-3" data-testid="public-profile-name">
              {p.name}
            </h1>
            {p.bio_short && (
              <p className="text-lg text-white/80 mb-4" data-testid="public-profile-bio-short">{p.bio_short}</p>
            )}
            {p.bio && (
              <p className="text-white/70 max-w-2xl whitespace-pre-line mb-4" data-testid="public-profile-bio">{p.bio}</p>
            )}
            <div className="flex flex-wrap gap-3 mt-4">
              {p.instagram_url && (
                <a href={p.instagram_url} target="_blank" rel="noreferrer" className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="public-profile-ig">
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
              )}
              {p.website_url && (
                <a href={p.website_url} target="_blank" rel="noreferrer" className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="public-profile-web">
                  <Globe className="w-4 h-4" /> Hemsida
                </a>
              )}
              <Link to={`/search?photographer=${p.id}`} className="btn-secondary text-sm py-2 inline-flex items-center gap-2">
                <Camera className="w-4 h-4" /> Se alla foton
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Photos */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="label-overline mb-2">Senaste fotona</p>
        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl mb-6">
          Galleri
        </h2>
        {photos.length === 0 ? (
          <p className="text-white/40 text-center py-12">Inga foton uppladdade ännu.</p>
        ) : (
          <>
            <BundleBanner unitPrice={profileUnitPrice} className="mb-4" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="public-profile-gallery">
              {photos.map((photo, i) => (
                <PhotoCard key={photo.id} photo={photo} onClick={() => setLightboxIndex(i)} hidePhotographer />
              ))}
            </div>
          </>
        )}
      </section>
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
      <Footer />
    </div>
  );
}
